import { useEffect, useState } from 'react';
import { Icon } from '../ui/Icon';

/**
 * Cosa sta succedendo davvero su QUESTO telefono.
 *
 * Perche' esiste. Due difetti riportati da Lorenzo il 22/09/2026 — una barra
 * larga in basso che sparisce scorrendo, e lo scorrimento che a volte non
 * parte — non si riproducono ne' sul computer ne' in emulazione: sono
 * comportamenti del browser vero su un telefono vero. Senza un numero preso
 * li' si tira a indovinare, e si e' gia' tirato a indovinare una volta.
 *
 * La domanda che vale piu' di tutte e' la prima: **l'app sta girando a tutto
 * schermo o dentro Safari?** Se e' dentro Safari, quella barra in basso e' la
 * sua, e nessuna riga di CSS nostro la togliera' mai.
 *
 * Si apre da "Altro", in fondo. Il tasto copia tutto negli appunti cosi' il
 * dato arriva come testo, non come foto di uno schermo.
 */

interface Riga { voce: string; valore: string; nota?: string }

function raccogli(): Riga[] {
  const mm = (q: string) => (window.matchMedia ? window.matchMedia(q).matches : false);
  const vv = window.visualViewport;
  const main = document.querySelector('.os-main');
  const stile = getComputedStyle(document.documentElement);
  const tacca = (lato: string) => {
    // Le safe-area non si leggono da JS: si fanno calcolare al CSS su un nodo
    // usa e getta, che e' l'unico modo onesto di sapere quanto vale la tacca.
    const spia = document.createElement('div');
    spia.style.cssText = `position:fixed;visibility:hidden;height:env(safe-area-inset-${lato},0px)`;
    document.body.appendChild(spia);
    const h = getComputedStyle(spia).height;
    spia.remove();
    return h;
  };

  /** Il rettangolo di un elemento in coordinate schermo, arrotondato. */
  const bordi = (el: Element | null): string => {
    if (!el) return 'non trovato';
    const r = el.getBoundingClientRect();
    return `da ${Math.round(r.top)} a ${Math.round(r.bottom)} (alto ${Math.round(r.height)})`;
  };

  /** Quanto vale davvero una misura di altezza su questo telefono. */
  const sonda = (altezza: string): number => {
    const s = document.createElement('div');
    s.style.cssText = `position:fixed;top:0;left:0;width:1px;visibility:hidden;height:${altezza}`;
    document.body.appendChild(s);
    const h = Math.round(s.getBoundingClientRect().height);
    s.remove();
    return h;
  };

  // iOS marca le app aggiunte alla schermata home con questa proprieta' sua.
  const standaloneApple = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  const standaloneCss = mm('(display-mode: standalone)') || mm('(display-mode: fullscreen)') || mm('(display-mode: minimal-ui)');
  const aTuttoSchermo = standaloneCss || standaloneApple === true;

  return [
    {
      voce: 'Come gira',
      valore: aTuttoSchermo ? 'App a tutto schermo' : 'Dentro il browser',
      nota: aTuttoSchermo
        ? 'nessuna barra del browser: quella in basso, se la vedi, e la nostra'
        : 'la barra in basso e quella del browser, e sparisce scorrendo: non e un difetto dell app',
    },
    { voce: 'display-mode', valore: standaloneCss ? 'standalone' : 'browser' },
    { voce: 'navigator.standalone', valore: String(standaloneApple) },
    { voce: 'Finestra', valore: `${window.innerWidth} x ${window.innerHeight}` },
    { voce: 'Viewport visibile', valore: vv ? `${Math.round(vv.width)} x ${Math.round(vv.height)} (zoom ${vv.scale.toFixed(2)})` : 'non disponibile' },
    { voce: 'Schermo', valore: `${window.screen.width} x ${window.screen.height}` },
    { voce: 'Tacca sopra / sotto', valore: `${tacca('top')} / ${tacca('bottom')}` },
    { voce: 'Guscio alto', valore: stile.getPropertyValue('--m-topbar-h').trim() || '—' },
    {
      voce: 'Cosa scorre',
      valore: main ? `${Math.round(main.scrollHeight)} dentro ${Math.round(main.clientHeight)}` : 'contenitore non trovato',
      nota: main && main.scrollHeight <= main.clientHeight + 1 ? 'non c e niente da scorrere in questa schermata' : undefined,
    },
    {
      voce: 'Pagina',
      valore: `${document.documentElement.scrollHeight} dentro ${document.documentElement.clientHeight}`,
      nota: document.documentElement.scrollHeight > document.documentElement.clientHeight + 1
        ? 'la PAGINA scorre, e non dovrebbe mai: e il difetto della striscia in basso'
        : 'la pagina non scorre: giusto cosi',
    },
    // Dove sta il documento rispetto allo schermo: e' la misura che dice se la
    // finestra e il documento cominciano nello stesso punto o no. Se il
    // documento e' alto meno della finestra, qui si vede DOVE avanza lo spazio.
    { voce: 'Documento sullo schermo', valore: bordi(document.documentElement) },
    { voce: 'Guscio', valore: bordi(document.querySelector('.os-shell')) },
    { voce: 'Barra in basso', valore: bordi(document.querySelector('.m-tabbar')) },
    { voce: 'Scroll pagina', valore: `${Math.round(window.scrollY)} / ${Math.round(document.scrollingElement?.scrollTop ?? 0)}` },
    {
      // La domanda che conta quando "resta una barra in basso": quanto schermo
      // il sistema NON ci ha dato. Se e' zero, l'app arriva al bordo davvero.
      voce: 'Schermo non usato',
      valore: `${Math.max(0, window.screen.height - window.innerHeight)}px`,
      nota: window.screen.height - window.innerHeight > 1
        ? 'questa e la striscia in fondo: e fuori dalla pagina, la dipinge solo il colore di sfondo'
        : 'nessuna striscia: la pagina prende tutto lo schermo',
    },
    // Le due misure che su quell'iPhone non coincidevano: 100dvh dava 852 e il
    // documento 793. Affiancate, il difetto si vede in un colpo. (Il 100% non
    // si misura con una sonda: dentro un elemento fisso la percentuale si
    // riferisce alla finestra, non al documento, e direbbe sempre 852.)
    {
      voce: '100dvh vs documento',
      valore: `${sonda('100dvh')} vs ${document.documentElement.clientHeight}`,
      nota: sonda('100dvh') !== document.documentElement.clientHeight
        ? 'se non coincidono, il guscio non deve MAI usare dvh'
        : 'coincidono: qui dvh sarebbe stato innocuo',
    },
    { voce: 'Browser', valore: navigator.userAgent.slice(0, 120) },
  ];
}

export function MobileDiagnostica() {
  const [aperta, setAperta] = useState(false);
  const [righe, setRighe] = useState<Riga[]>([]);
  const [copiato, setCopiato] = useState(false);

  useEffect(() => {
    if (!aperta) return;
    setRighe(raccogli());
    // Le misure cambiano mentre la barra del browser si ritira: tenerle vive
    // e' proprio il punto, cosi' si vede il prima e il dopo.
    const aggiorna = () => setRighe(raccogli());
    window.visualViewport?.addEventListener('resize', aggiorna);
    window.addEventListener('resize', aggiorna);
    return () => {
      window.visualViewport?.removeEventListener('resize', aggiorna);
      window.removeEventListener('resize', aggiorna);
    };
  }, [aperta]);

  const testo = righe.map((r) => `${r.voce}: ${r.valore}`).join('\n');

  const copia = async () => {
    try {
      await navigator.clipboard.writeText(testo);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    } catch {
      // Senza permesso sugli appunti resta la selezione a mano: il testo e' li'.
      setCopiato(false);
    }
  };

  return (
    <div className="m-diag">
      <button type="button" className="m-sheet-row" onClick={() => setAperta((v) => !v)}>
        <Icon name="help" size={17} />
        <span className="m-sheet-row-lbl">Diagnostica schermo</span>
        <Icon name={aperta ? 'chevronD' : 'chevronR'} size={14} className="m-sheet-row-go" />
      </button>

      {aperta && (
        <div className="m-diag-corpo">
          {righe.map((r) => (
            <div className="m-diag-riga" key={r.voce}>
              <span className="m-diag-voce">{r.voce}</span>
              <span className="m-diag-valore">{r.valore}</span>
              {r.nota && <span className="m-diag-nota">{r.nota}</span>}
            </div>
          ))}
          <button type="button" className="btn btn-sm m-diag-copia" onClick={copia}>
            <Icon name={copiato ? 'check' : 'copy'} size={12} /> {copiato ? 'Copiato' : 'Copia tutto'}
          </button>
        </div>
      )}
    </div>
  );
}
